import {
  type ComponentProps,
  type ReactNode,
  useEffect,
  useState,
} from 'react';
import { AlertComponent } from '~/_shared/baseComponents/alert';
import {
  ButtonComponent, ButtonSize,
  ButtonStyle,
} from '~/_shared/baseComponents/buttons';
import { MapTool } from '~/_shared/types/toolsAndFeatures/mapTools.types';
import { useTranslation } from '~/_shared/utils/hooks';
import type { BoundaryGroup } from '~/boundary/boundary.types';
import { DataLayersToolListingContainer } from '~/sidebar/sidebarApps/mapTools/dataLayersTool/listing/dataLayersToolListing.container';
import { DataLayersToolChangeBoundaryComponent } from '~/sidebar/sidebarApps/mapTools/dataLayersTool/menu/dataLayersToolChangeBoundary.component';
import { DataLayersToolChoicesComponent } from '~/sidebar/sidebarApps/mapTools/dataLayersTool/menu/dataLayersToolChoices.component';
import { mapToolIconMap } from '~/sidebar/sidebarApps/mapTools/mapTools.helpers';
import type { DataLayerTemplateResponse } from '~/store/frontendState/mapTools/dataLayers/dataLayers.repository';
import { MapToolWrapperComponent } from '../../mapToolWrapper/mapToolWrapper.component';
import {
  alertStyle,
  buttonStyle,
  formStyle,
} from './dataLayersTool.styles';

export enum DataLayerAction {
  ChangeBoundaryGroup,
  CreateNewLayerGroup,
  ChooseOptions,
}

export const DataLayersToolComponent = ({
  changeBoundaryGroupProps,
  hasActiveDataLayer,
  error,
  isLoading,
  isLegendMode,
  boundaryGroups,
  savedLayerGroups,
}: {
  changeBoundaryGroupProps: Omit<ComponentProps<typeof DataLayersToolChangeBoundaryComponent>, 'onDone'>;
  hasActiveDataLayer: boolean;
  error?: ReactNode;
  isLoading?: boolean;
  isLegendMode: boolean;
  boundaryGroups: ReadonlyArray<BoundaryGroup>;
  savedLayerGroups: ReadonlyArray<DataLayerTemplateResponse>;
}) => {
  const [t] = useTranslation();

  const [action, setAction] = useState<DataLayerAction>(
    hasActiveDataLayer ? DataLayerAction.ChooseOptions : DataLayerAction.CreateNewLayerGroup,
  );

  const backToDefaultAction = () => {
    if (hasActiveDataLayer) {
      setAction(DataLayerAction.ChooseOptions);
    }
    else {
      setAction(DataLayerAction.CreateNewLayerGroup);
    }
  };

  useEffect(() => {
    if (!hasActiveDataLayer) {
      setAction(DataLayerAction.CreateNewLayerGroup);
    }
  }, [hasActiveDataLayer]);

  return (
    <MapToolWrapperComponent isLoading={isLoading}>
      <div>

        {!isLegendMode && (
          <div css={formStyle}>
            {action === DataLayerAction.ChooseOptions && (
              <>
                <ButtonComponent
                  buttonStyle={ButtonStyle.Primary}
                  css={buttonStyle}
                  onClick={() => setAction(DataLayerAction.CreateNewLayerGroup)}
                  prefixIcon={mapToolIconMap[MapTool.DataLayers]}
                  size={ButtonSize.Large}
                  text={t('dataLayersTool.addAnotherLayerGroup')}
                />
                <ButtonComponent
                  buttonStyle={ButtonStyle.Secondary}
                  css={buttonStyle}
                  prefixIcon={mapToolIconMap[MapTool.Boundary]}
                  onClick={() => setAction(DataLayerAction.ChangeBoundaryGroup)}
                  size={ButtonSize.Large}
                  text={t('dataLayersTool.changeBoundaryType')}
                />
              </>
            )}
            {action === DataLayerAction.ChangeBoundaryGroup && (
              <>
                <DataLayersToolChangeBoundaryComponent
                  {...changeBoundaryGroupProps}
                  onDone={backToDefaultAction}
                />
                <ButtonComponent
                  buttonStyle={ButtonStyle.Secondary}
                  css={buttonStyle}
                  onClick={backToDefaultAction}
                  size={ButtonSize.Large}
                  text={t('Cancel')}
                />
              </>
            )}
            {action === DataLayerAction.CreateNewLayerGroup && (
              <>
                <DataLayersToolChoicesComponent
                  boundaryGroups={boundaryGroups}
                  savedLayerGroups={savedLayerGroups}
                  onDone={backToDefaultAction}
                />
                {hasActiveDataLayer && (
                  <ButtonComponent
                    buttonStyle={ButtonStyle.Secondary}
                    css={buttonStyle}
                    onClick={backToDefaultAction}
                    size={ButtonSize.Large}
                    text={t('Cancel')}
                  />
                )}
              </>
            )}

            {error && (
              <AlertComponent
                size="small"
                type="danger"
                css={alertStyle}
              >
                {error}
              </AlertComponent>
            )}

          </div>
        )}

      </div>

      <div>
        <DataLayersToolListingContainer />
      </div>
    </MapToolWrapperComponent>
  );
};
